import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from '@/components/foundations';

export type ButtonVariant = 'gold' | 'outline-gold' | 'outline-navy';

type Common = {
  variant?: ButtonVariant;
  /** Ocupa toda a largura (`btn-block`). */
  block?: boolean;
  /** `lg` = botão de ação do cabeçalho de página (50px de altura). */
  size?: 'md' | 'lg';
  icon?: IconName;
  loading?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

export type ButtonProps = Common &
  (
    | ({ to: string; href?: never } & { onClick?: () => void })
    | ({ href: string; to?: never } & { onClick?: () => void })
    | ({ to?: never; href?: never } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'style' | 'children'>)
  );

const LG: CSSProperties = { height: 50, padding: '0 24px' };

/** Botão do pacote (`.btn` + `btn-gold` / `btn-outline-gold` / `btn-outline-navy`). Vira `Link` com `to` e `<a>` com `href`. */
export function Button(props: ButtonProps) {
  const { variant = 'gold', block, size = 'md', icon, loading, className = '', style, children, ...rest } = props;
  const cls = ['btn', `btn-${variant}`, block ? 'btn-block' : '', className].filter(Boolean).join(' ');
  const st = size === 'lg' ? { ...LG, ...style } : style;
  const content = (
    <>
      {icon && <Icon name={loading ? 'refresh' : icon} />}
      {children}
    </>
  );
  if ('to' in rest && rest.to) return <Link className={cls} style={st} to={rest.to} onClick={rest.onClick}>{content}</Link>;
  if ('href' in rest && rest.href) return <a className={cls} style={st} href={rest.href} onClick={rest.onClick}>{content}</a>;
  const { disabled, ...btn } = rest as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button type="button" className={cls} style={st} disabled={disabled || loading} aria-busy={loading || undefined} {...btn}>
      {content}
    </button>
  );
}
